import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  GoogleMap,
  useLoadScript,
  OverlayView,
  Marker,
  DirectionsRenderer,
} from "@react-google-maps/api";
import config from "../../config.json";
import {
  Typography,
  CircularProgress,
  Box,
  Divider,
  Button,
  Select,
  MenuItem
} from "@mui/material";
import busIcon from "../../images/vehicle/Map_Vehicle_active.svg";
import RouteVehicleStatusTable from "./RouteVehicleStatusTable";
import { useNavigate } from "react-router-dom";
import NavMenu from "../../components/navbar/NavMenu";
import TitleBar from "../../components/navbar/TitleBar";
import { useTranslation } from "react-i18next";
import MonitorSubtitle from "../../components/common/MonitorSubtitle";

const styles = {
  container: { padding: "32px" },
  cardSection: { marginBottom: "32px" },
  card: {
    padding: "20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderRadius: "12px",
    color: "white",
  },
  vehicleCard: { backgroundColor: "rgba(88, 0, 191, 1)" },
  stopCard: { backgroundColor: "rgba(0, 178, 191, 1)" },
  scheduleCard: { backgroundColor: "rgba(191, 80, 0, 1)" },
  cardContent: { display: "flex", flexDirection: "column" },
  icon: { fontSize: "40px", marginTop: "10px" },
  mapContainer: { width: "100%", height: "400px", marginBottom: "32px" },
  tableContainer: { marginTop: "32px" },
  table: { width: "100%", borderCollapse: "collapse", marginTop: "-7px" },
  tableHeader: { padding: "8px", textAlign: "left", backgroundColor: "#f5f5f5" },
  tableCell: { border: "1px solid #ddd", padding: "8px" },
  noData: { textAlign: "center", padding: "46px", height: "auto" },
  formHeader: { fontSize: "1.2rem", fontWeight: "bold", marginBottom: "16px" },
};

const mapStyles = [
  { featureType: "all", elementType: "all", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ visibility: "on" }, { color: "black" }] },
];

const libraries = ["geometry"];
const colors =[
  "#20A8E0", "#8FBC8F", "#FFD700", "#6495ED", "#FF69B4", "#FFA07A", "#ADFF2F", "#FF4500", "#9370DB", "#FFFFFF",
];

const normalizeDeviceData = (data) => {
  const lat = parseFloat(data.Latitude);
  const lng = parseFloat(data.Longitude);
  if (isNaN(lat) || isNaN(lng)) {
    console.warn(`Invalid coordinates for device ${data.device_id}`);
    return null;
  }
  const rawDataParts = data.RawData ? data.RawData.split(",") : [];
  const satellites = data.UsedSatellites?.GPSSatellitesCount || rawDataParts[1] || "N/A";
  const temperature = data.EnvironmentalData?.Temperature || "N/A";

  return {
    deviceId: data.device_id,
    latitude: lat.toString(),
    longitude: lng.toString(),
    timestamp: new Date().toISOString(),
    rawData: data.RawData || "",
    altitude: rawDataParts[9] ? parseFloat(rawDataParts[9]) : null,
    speed: rawDataParts[11] ? parseFloat(rawDataParts[11]) : 0,
    satellites,
    temperature,
  };
};

const calculateDistanceAndETA = (vehiclePos, routePath, stops, vehicleSpeed) => {
  if (!routePath || routePath.length === 0) return { distance: 0, eta: 0 };

  const vehiclePoint = new window.google.maps.LatLng(vehiclePos.lat, vehiclePos.lng);
  let minDistance = Infinity;
  let closestIndex = 0;

  routePath.forEach((point, index) => {
    const distance = window.google.maps.geometry.spherical.computeDistanceBetween(vehiclePoint, point);
    if (distance < minDistance) {
      minDistance = distance;
      closestIndex = index;
    }
  });

  const remainingPath = routePath.slice(closestIndex);
  const totalDistance = remainingPath.reduce((acc, point, i) => {
    if (i === 0) return acc;
    return acc + window.google.maps.geometry.spherical.computeDistanceBetween(remainingPath[i - 1], point);
  }, 0) / 1000; // Convert to km

  const avgSpeed = vehicleSpeed || 10; // Default speed in km/h if not available
  const timeInSeconds = (totalDistance / avgSpeed) * 3600; // Convert to seconds

  let cumulativeDuration = 0;
  stops.forEach((stop, index) => {
    if (index >= closestIndex) {
      cumulativeDuration += parseInt(stop.durationValue || 60); // Default 1 min if not available
    }
  });

  return {
    distance: totalDistance.toFixed(2),
    eta: timeInSeconds + cumulativeDuration,
  };
};

const RouteTrackingComponent = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const Navigate = useNavigate();
  const [selectedStop, setSelectedStop] = useState(null);
  const routeData = location.state?.selectedRouteData;
  const [vehicleData, setVehicleData] = useState({});
  const [vehicleLocations, setVehicleLocations] = useState([]);
  const [vehicleIds, setVehicleIds] = useState([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [directions, setDirections] = useState(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const eventSourceRef = useRef(null);
  const routePathRef = useRef(null);
  const routeBoundsRef = useRef(null);
  const [labelPositions, setLabelPositions] = useState({});
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: config.googleMapsApiKey,
    libraries,
  });

  const getBackgroundColor = (deviceId) => {
    const index = Math.abs(hashString(deviceId)) % colors.length;
    return colors[index];
  };

  const hashString = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
      hash = hash & hash;
    }
    return hash;
  };

  const formatDistance = (distance) => {
    if (!distance) return "N/A";
    return `${parseFloat(distance).toFixed(1)} km`;
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds <= 0) return "N/A";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  useEffect(() => {
    if (routeData?.vehicles?.length > 0) {
      const ids = routeData.vehicles.map((vehicle) => vehicle.device_id);
      console.log("Vehicle IDs:", ids);
      setVehicleIds(ids);
    } else {
      console.warn("No vehicles found in routeData");
    }
  }, [routeData]);

  useEffect(() => {
    const updatedVehicleData = vehicleLocations.reduce((acc, location) => {
      if (location.deviceId && location.latitude && location.longitude) {
        const position = { lat: parseFloat(location.latitude), lng: parseFloat(location.longitude) };
        const { distance, eta } = calculateDistanceAndETA(position, routePathRef.current, routeData.stops, location.speed);
        acc[location.deviceId] = {
          device_id: location.deviceId,
          position,
          timestamp: location.timestamp,
          speed: location.speed || 0,
          altitude: location.altitude,
          satellites: location.satellites,
          temperature: location.temperature,
          distance,
          eta,
        };
      }
      return acc;
    }, {});
    console.log("Updated vehicleData:", updatedVehicleData);
    setVehicleData(updatedVehicleData);
    setLastUpdateTime(new Date().toLocaleString());
    setLoading(false);
  }, [vehicleLocations, routeData.stops]);

  const fetchAllVehicleLocations = useCallback(() => {
    if (vehicleIds.length === 0) return;
    const query = vehicleIds.map((id) => `vehicleId=${id}`).join("&");
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }
    const sseUrl = `${config.baseURL}/redis/stream?${query}`;
    const source = new EventSource(sseUrl);
    eventSourceRef.current = source;

    source.onopen = () => console.log("SSE connection established");
    source.onerror = (error) => {
      console.error("SSE error:", error);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      setTimeout(fetchAllVehicleLocations, 5000);
    };
    source.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log("Received SSE data:", data);
        if (data.type === "connected") {
          console.log("SSE connected:", data);
          return;
        }
        if (data.device_id && data.Latitude && data.Longitude) {
          const normalizedData = normalizeDeviceData(data);
          if (normalizedData) {
            setVehicleLocations((prev) => {
              const existingIndex = prev.findIndex((item) => item.deviceId === normalizedData.deviceId);
              if (existingIndex >= 0) {
                const newLocations = [...prev];
                newLocations[existingIndex] = normalizedData;
                return newLocations;
              }
              return [...prev, normalizedData];
            });
          }
        } else {
          console.warn("Unexpected SSE data format:", data);
        }
      } catch (e) {
        console.error("Error parsing SSE data:", e);
      }
    };
  }, [vehicleIds]);

  useEffect(() => {
    if (vehicleIds.length > 0) {
      fetchAllVehicleLocations();
      return () => {
        if (eventSourceRef.current) eventSourceRef.current.close();
      };
    }
  }, [vehicleIds, fetchAllVehicleLocations]);

  const center = useMemo(() => {
    if (routeData?.routeCoordinates?.[0]) {
      return {
        lat: parseFloat(routeData.routeCoordinates[0].latitude),
        lng: parseFloat(routeData.routeCoordinates[0].longitude),
      };
    }
    if (routeData?.stops?.[0]) {
      return {
        lat: parseFloat(routeData.stops[0].latitude),
        lng: parseFloat(routeData.stops[0].longitude),
      };
    }
    return { lat: 13.1986, lng: 77.7099 };
  }, [routeData]);

  const stopMarkers = useMemo(() => {
    return routeData
      ? routeData.stops.map((stop) => ({
          position: { lat: parseFloat(stop.latitude), lng: parseFloat(stop.longitude) },
          label: stop.name || "Unknown",
        }))
      : [];
  }, [routeData]);

  const calculateRouteDirections = useCallback(() => {
    if (!window.google || !routeData?.routeCoordinates?.length) return;
    const directionsService = new window.google.maps.DirectionsService();
    const origin = {
      lat: parseFloat(routeData.routeCoordinates[0].latitude),
      lng: parseFloat(routeData.routeCoordinates[0].longitude),
    };
    const destination = {
      lat: parseFloat(routeData.routeCoordinates[routeData.routeCoordinates.length - 1].latitude),
      lng: parseFloat(routeData.routeCoordinates[routeData.routeCoordinates.length - 1].longitude),
    };
    const waypoints = routeData.routeCoordinates.slice(1, -1).map((coord) => {
      const isStop = routeData.stops.some(
        (stop) =>
          parseFloat(stop.latitude) === parseFloat(coord.latitude) &&
          parseFloat(stop.longitude) === parseFloat(coord.longitude)
      );
      return {
        location: new window.google.maps.LatLng(parseFloat(coord.latitude), parseFloat(coord.longitude)),
        stopover: isStop,
      };
    });
    const request = {
      origin: new window.google.maps.LatLng(origin.lat, origin.lng),
      destination: new window.google.maps.LatLng(destination.lat, destination.lng),
      waypoints,
      travelMode: window.google.maps.TravelMode.DRIVING,
      optimizeWaypoints: false,
    };
    directionsService.route(request, (result, status) => {
      if (status === window.google.maps.DirectionsStatus.OK) {
        console.log("Directions received:", result);
        setDirections(result);
        routePathRef.current = result.routes[0].overview_path;
        if (mapInstance && !routeBoundsRef.current) {
          const bounds = new window.google.maps.LatLngBounds();
          result.routes[0].overview_path.forEach((point) => bounds.extend(point));
          stopMarkers.forEach((marker) => bounds.extend(marker.position));
          routeBoundsRef.current = bounds;
          mapInstance.fitBounds(bounds);
          window.google.maps.event.addListenerOnce(mapInstance, "idle", () => {
            const currentZoom = mapInstance.getZoom();
            if (currentZoom > 15) mapInstance.setZoom(15);
            else if (currentZoom < 10) mapInstance.setZoom(10);
          });
        }
      } else {
        console.error("Directions request failed:", status);
        setError(`Error fetching directions: ${status}`);
      }
    });
  }, [routeData?.routeCoordinates, routeData?.stops, mapInstance, stopMarkers]);

  const onLoad = useCallback((map) => {
    map.setCenter(center);
    map.setZoom(17);
    setMapLoaded(true);
    setMapInstance(map);
  }, [center]);

  const calculateLabelPositions = useCallback((vehicles) => {
    const positions = {};
    const LABEL_HEIGHT = 30;
    const groupedVehicles = [];
    vehicles.forEach((vehicle) => {
      if (!vehicle || !vehicle.position) return;
      let foundGroup = false;
      for (const group of groupedVehicles) {
        const firstVehicle = group[0];
        const distance = window.google.maps.geometry.spherical.computeDistanceBetween(
          new window.google.maps.LatLng(vehicle.position.lat, vehicle.position.lng),
          new window.google.maps.LatLng(firstVehicle.position.lat, firstVehicle.position.lng)
        );
        if (distance < 50) {
          group.push(vehicle);
          foundGroup = true;
          break;
        }
      }
      if (!foundGroup) groupedVehicles.push([vehicle]);
    });
    groupedVehicles.forEach((group, groupIndex) => {
      group.forEach((vehicle, index) => {
        positions[vehicle.device_id] = LABEL_HEIGHT * (groupIndex + index);
      });
    });
    return positions;
  }, []);

  useEffect(() => {
    if (Object.values(vehicleData).length > 0) {
      const newPositions = calculateLabelPositions(Object.values(vehicleData));
      setLabelPositions(newPositions);
    }
  }, [vehicleData, calculateLabelPositions]);

  // Run calculateRouteDirections only when mapLoaded, routeData, or mapInstance changes
  useEffect(() => {
    if (mapLoaded && routeData && mapInstance) {
      calculateRouteDirections();
    }
  }, [mapLoaded, routeData, mapInstance, calculateRouteDirections]);

  // Update map bounds when vehicleData changes, without triggering route recalculation
  useEffect(() => {
    if (mapInstance && routeBoundsRef.current && Object.values(vehicleData).length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      // Extend bounds with the original route bounds
      bounds.union(routeBoundsRef.current);
      // Extend bounds with stop markers
      stopMarkers.forEach((marker) => bounds.extend(marker.position));
      // Extend bounds with vehicle positions
      Object.values(vehicleData).forEach((vehicle) => bounds.extend(vehicle.position));
      mapInstance.fitBounds(bounds);
      window.google.maps.event.addListenerOnce(mapInstance, "idle", () => {
        const currentZoom = mapInstance.getZoom();
        if (currentZoom > 15) mapInstance.setZoom(15);
        else if (currentZoom < 10) mapInstance.setZoom(10);
      });
    }
  }, [vehicleData, mapInstance, stopMarkers]);

  const routeDetailsUseriew = () => {
    try {
      console.log("Original routeData:", routeData);

      if (!routeData) {
        console.error("No route data available");
        return;
      }

      const routeDataForTracking = {
        id: routeData.id,
        name: routeData.name,
        routeId: routeData.routeId,
        stops: routeData.stops || [],
        vehicles: routeData.vehicles || [],
        routeCoordinates: routeData.routeCoordinates || [],
        schedules: routeData.schedules || []
      };

      console.log("Prepared routeDataForTracking:", routeDataForTracking);

      Navigate(`/route/screen/${routeData.id}`, {
        state: {
          selectedRouteData: routeDataForTracking
        }
      });
    } catch (error) {
      console.error("Error in routeDetailsUseriew:", error);
    }
  };

  const handleStopChange = (e) => {
    const stopId = e.target.value;

    if (stopId === "") {
      setSelectedStop(null);
      if (mapInstance && routeBoundsRef.current) {
        mapInstance.fitBounds(routeBoundsRef.current);
        window.google.maps.event.addListenerOnce(mapInstance, "idle", () => {
          const currentZoom = mapInstance.getZoom();
          if (currentZoom > 15) mapInstance.setZoom(15);
          else if (currentZoom < 10) mapInstance.setZoom(10);
        });
      }
    } else {
      const stop = routeData.stops.find(stop => stop.id === stopId);
      if (stop) {
        setSelectedStop(stop);
        if (mapInstance) {
          const position = {
            lat: parseFloat(stop.latitude),
            lng: parseFloat(stop.longitude)
          };
          mapInstance.panTo(position);
          mapInstance.setZoom(17);
        }
      }
    }
  };

  if (!routeData) {
    return (
      <>
        <TitleBar />
        <NavMenu />
        <div style={styles.noData}>
          <Typography variant="h6">No route data available</Typography>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <TitleBar />
        <NavMenu />
        <div style={styles.noData}>
          <Typography color="error">{error}</Typography>
        </div>
      </>
    );
  }

  if (loadError) {
    return (
      <>
        <TitleBar />
        <NavMenu />
        <div style={styles.noData}>
          <Typography color="error">Failed to load Google Maps</Typography>
        </div>
      </>
    );
  }

  if (!isLoaded) {
    return (
      <>
        <TitleBar />
        <NavMenu />
        <div style={styles.noData}>
          <CircularProgress />
        </div>
      </>
    );
  }

  return (
    <>
      <TitleBar />
      <NavMenu />
      <MonitorSubtitle
        title="Monitor Route"
        path="Routes > Monitor Route"
        track={"Route View"}
        onTrackClick={() => routeDetailsUseriew()} />
      <Box
        sx={{
          borderRadius: "10px",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          padding: { xs: "15px", sm: "15px 30px", md: "20px 60px" },
          backgroundColor: "#FFFFFF",
          border: "1px solid #A5DCF3",
          margin: { xs: "0px 15px", sm: "0px 20px", md: "0px 40px" },
          boxShadow: "-2px 1px 5px 1px #00000040",
          gap: { xs: 2, sm: 0 }
        }}
      >
        <Box sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flex: 1,
          mb: { xs: 2, sm: 0 }
        }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: { xs: "16px", md: "20px" },
                lineHeight: "100%",
                letterSpacing: "0%",
                textAlign: "center",
                color: "#183059"
              }}
            >
              Total Vehicles
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: { xs: "20px", md: "24px" },
              lineHeight: "100%",
              letterSpacing: "0%",
              textAlign: "center"
            }}
          >
            {routeData.vehicles.length}
          </Typography>
        </Box>
        <Divider
          orientation={{ xs: "horizontal", sm: "vertical" }}
          flexItem
          sx={{
            mx: { sm: 2 },
            my: { xs: 2, sm: 0 },
            height: { xs: "1px", sm: "60px" },
            width: { xs: "100%", sm: "auto" },
            alignSelf: "center",
            border: "1px solid #183059"
          }}
        />
        <Box sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flex: 1,
          mb: { xs: 2, sm: 0 }
        }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: { xs: "16px", md: "20px" },
                lineHeight: "100%",
                letterSpacing: "0%",
                textAlign: "center",
                color: "#183059"
              }}
            >
              Total Stops
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: { xs: "20px", md: "24px" },
              lineHeight: "100%",
              letterSpacing: "0%",
              textAlign: "center"
            }}
          >
            {routeData.stops.length}
          </Typography>
        </Box>
        <Divider
          orientation={{ xs: "horizontal", sm: "vertical" }}
          flexItem
          sx={{
            mx: { sm: 2 },
            my: { xs: 2, sm: 0 },
            height: { xs: "1px", sm: "60px" },
            width: { xs: "100%", sm: "auto" },
            alignSelf: "center",
            border: "1px solid #183059"
          }}
        />
        <Box sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flex: 1
        }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: { xs: "16px", md: "20px" },
                lineHeight: "100%",
                letterSpacing: "0%",
                textAlign: "center",
                color: "#183059"
              }}
            >
              Total Schedules
            </Typography>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="body1"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: { xs: "20px", md: "24px" },
                lineHeight: "100%",
                letterSpacing: "0%",
                textAlign: "center"
              }}
            >
              {routeData.schedules.length}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{
        borderRadius: "10px",
        padding: { xs: "10px", sm: "15px", md: "20px" },
        backgroundColor: "#FFFFFF",
        border: "1px solid #A5DCF3",
        margin: { xs: "10px 15px", sm: "15px 20px", md: "20px 40px" },
        boxShadow: "-2px 1px 5px 1px #00000040"
      }}>
        <Box
          sx={{
            minHeight: "48px",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            padding: { xs: "12px", sm: "12px 16px" },
            background: "#E8EAEE",
            boxShadow: "0px 2px 5px 0px #00000040",
            gap: { xs: 2, sm: 0 }
          }}
        >
          <Box sx={{
            display: "flex",
            alignItems: "center",
            width: { xs: "100%", sm: "auto" }
          }}>
            <Typography
              sx={{
                color: "#414958",
                fontFamily: "Roboto",
                fontWeight: 400,
                fontSize: { xs: "16px", sm: "18px", md: "20px" },
                lineHeight: "100%",
                letterSpacing: "0%"
              }}
            >
              Route:
            </Typography>
            <Typography
              sx={{
                fontFamily: "Roboto",
                fontWeight: 600,
                fontSize: { xs: "16px", sm: "18px", md: "20px" },
                lineHeight: "100%",
                color: "#183059",
                letterSpacing: "0%",
                ml: 1,
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
            >
              {routeData.name || "---"}
            </Typography>
          </Box>
          <Box sx={{
            display: "flex",
            alignItems: "center",
            width: { xs: "100%", sm: "auto" }
          }}>
            <Select
              value={selectedStop ? selectedStop.id : ""}
              onChange={handleStopChange}
              displayEmpty
              inputProps={{ 'aria-label': 'Without label' }}
              sx={{
                height: "30px",
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: "150px" }
              }}
            >
              <MenuItem value="">
                Stops
              </MenuItem>
              {routeData.stops.map((stop) => (
                <MenuItem key={stop.id} value={stop.id}>
                  {stop.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
        <GoogleMap
          mapContainerStyle={{
            width: "100%", height: "400px", border: "1px solid #000000",
            marginTop: "20px",
            borderRadius: "20px"
          }}
          center={center}
          onLoad={onLoad}
          options={{ minZoom: 13, maxZoom: 30, styles: mapStyles, zoom: 15 }}
        >
          {directions && (
            <DirectionsRenderer
              directions={directions}
              options={{
                suppressMarkers: true,
                polylineOptions: { strokeColor: routeData.routeColor || "#0000FF", strokeOpacity: 0.8, strokeWeight: 4 },
              }}
            />
          )}
          {stopMarkers.map((marker, index) => (
            <React.Fragment key={index}>
              <OverlayView
                position={marker.position}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <div style={{ position: "relative", top: "-62px", left: "0px" }}>
                  <div
                    style={{
                      position: "absolute",
                      left: "50%",
                      transform: "translateX(-50%)",
                      backgroundColor: selectedStop && marker.label === selectedStop.name ? "#ffff00" : "white",
                      color: index === 0 || index === stopMarkers.length - 1 ? "green" : "black",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      fontSize: index === 0 || index === stopMarkers.length - 1 ? "12px" : "10px",
                      fontWeight: (selectedStop && marker.label === selectedStop.name) ? "bold" :
                        (index === 0 || index === stopMarkers.length - 1) ? "700" : "500",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                      whiteSpace: "nowrap",
                      zIndex: 1000,
                    }}
                  >
                    {marker.label}
                  </div>
                </div>
              </OverlayView>
              <Marker   position={marker.position}
                icon={
                  selectedStop && marker.label === selectedStop.name
                    ? { url: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png", scaledSize: new window.google.maps.Size(40, 40) }
                    : index === 0 || index === stopMarkers.length - 1
                      ? { url: "http://maps.google.com/mapfiles/ms/icons/green-dot.png", scaledSize: new window.google.maps.Size(40, 40) }
                      : undefined
                }
              />
            </React.Fragment>
          ))}
          {Object.entries(vehicleData).map(([deviceId, data]) => (
            <React.Fragment key={deviceId}>
              <OverlayView position={data.position} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                <div style={{ position: "relative", top: "-30px", left: "0px" }}>
                  <div
                    style={{
                      position: "absolute",
                      width: "2px",
                      height: `${60 + labelPositions[deviceId]}px`,
                      backgroundColor: getBackgroundColor(deviceId),
                      bottom: "-20px",
                      left: "50%",
                      transform: "translateX(-50%)",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      bottom: `${40 + labelPositions[deviceId]}px`,
                      left: "50%",
                      transform: "translateX(-50%)",
                      color: "rgb(0, 0, 0)",
                      fontSize: "10px",
                      fontWeight: 600,
                      fontFamily: "Arial, sans-serif",
                      lineHeight: "14.06px",
                      padding: "8px",
                      textAlign: "left",
                      whiteSpace: "nowrap",
                      backgroundColor: getBackgroundColor(deviceId),
                      borderRadius: "12px",
                      zIndex: 1000,
                      border: "1px solid black",
                    }}
                  >
                    {deviceId}
                  </div>
                </div>
              </OverlayView>
              <Marker
                position={data.position}
                icon={{ url: busIcon, scaledSize: new window.google.maps.Size(32, 32), anchor: new window.google.maps.Point(16, 16) }}
              />
            </React.Fragment>
          ))}
        </GoogleMap>
      </Box>
      
      <RouteVehicleStatusTable
        loading={loading}
        error={error}
        vehicleData={vehicleData}
        formatDistance={formatDistance}
        formatTime={formatTime}
      />
      {lastUpdateTime && (
        <Typography
          variant="caption"
          sx={{ display: "block", textAlign: "right", mt: 1, color: "text.secondary" }}
        >
          Last updated: {lastUpdateTime}
        </Typography>
      )}
    </>
  );
};

export default RouteTrackingComponent